<template>
  <TheNavbar />

  <div class="mt-4 mb-3">
    <TheBotErrorsAlert />
  </div>

  <!-- <div v-if="hasCoinbaseBot" class="mt-4">
    <div class="alert alert-warning mb-0 d-flex align-items-center" role="alert">
      <span class="fas fa-exclamation-triangle me-2"></span>
      <div>
        <strong>Coinbase Bots</strong><br>
        Coinbase's API is currently intermittently returning empty Bot balances, which causes some Coinbase Bots to be missing from the Dashboard (they have no assets to show for).<br>
        This is an issue on Coinbase's side and we are working on a fix/workaround...
      </div>
    </div>
  </div> -->

  <div class="mt-5">
    <div v-if="loadingUserAssets">
      <div class="row justify-content-center mb-3">
        <div class="col-auto">
          <div class="mb-3 text-center">
            <p v-if="nowFetchingBot">
              Now fetching all assets from your <b>{{ nowFetchingBot.title }}</b> Bot from <b>{{ getExchangeById(nowFetchingBot.exchangeId)?.name }}</b>
            </p>
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    </div> 

    <div v-if="!loadingUserAssets">
      <div class="row mb-3">
        <div v-if="userFundsPerAsset && userFundsPerAsset?.length > 0" class="col-6 text-start">
          <div class="form-check form-switch">
            <input class="form-check-input" id="flexSwitchCheckChecked" type="checkbox" v-model="showDust" />
            <label class="form-check-label" for="flexSwitchCheckChecked">Show Dust (USD&lt;1)</label>
          </div>
        </div>
        <div class="col-6 col-sm-auto ms-auto text-end ps-0">
          <button @click="refreshUserAssets" class="btn btn-primary btn-sm" type="button">
            <span class="fas fa-sync-alt" data-fa-transform="shrink-3 down-2"></span>
            Refresh Assets
          </button>
        </div>
      </div>
      
      <div v-if="userFundsPerAsset && userFundsPerAsset?.length <= 0" class="row justify-content-center g-3 mb-3">
        <div class="col text-center">
          No assets found.<br>
          It seems none of your Bots has assets to trade with at the moment.
        </div>
      </div>

      <div v-if="!userFundsPerAsset" class="row justify-content-center g-3 mb-3">
        <div class="col text-center">
          <span class="mb-1">Create your first Bot now</span> &nbsp;
          <router-link :to="{name: 'CreateNewBot'}" class="btn btn-primary btn-sm" type="button">
            <span class="fas fa-plus" data-fa-transform="shrink-3 down-2"></span>
            Create Bot
          </router-link>
        </div>
      </div>


      <!-- Assets Table & Chart -->
      <div v-if="userFundsPerAsset && userFundsPerAsset?.length > 0" class="row g-3 mb-3">

        <div class="col-12 col-xl-6 col-lg-12 col-md-12">

          <!-- Total Net Worth -->
          <div v-if="totalAssetsValueUSD" class="card mb-3">
            <div class="card-header pb-0">
              <h6 class="mb-0 mt-2 d-flex align-items-center fw-bold">Total Net Worth</h6>
            </div>
            <div class="card-body d-flex flex-column justify-content-end">
              <div class="row">
                <div class="col-auto">
                  <p class="font-sans-serif lh-1 mb-1 fs-5">USD {{ getPrettyFloatNumber(totalAssetsValueUSD, 2) }}</p>
                  <p class="mt-2 mb-0 fs-11 text-center">Based on current Spot Assets & Positions (incl. unrealized P&L).</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Assets in % -->
          <div class="card mb-3">
            <div class="card-header">
              <h5 class="fs-6 mb-0 text-nowrap py-2 py-xl-0">Funds (%)</h5>
            </div>
            <div class="card-body">
              <PieChartWithLegend
                :chartData="userFundsPerAssetPieChartData"
              />
              <p class="mt-2 mb-0 fs-11 text-center">Based on current Spot Assets & Positions (incl. unrealized P&L) valued in USD. Rounded to nearest number.</p>
            </div>
          </div>

          <!-- Allocated assets in % -->
          <div class="card mb-3">
            <div class="card-header">
              <h5 class="fs-6 mb-0 text-nowrap py-2 py-xl-0">Funds Allocation (%)</h5>
            </div>
            <div class="card-body">
              <PieChartWithLegend
                :chartData="allUserAllocatedFundsPieChartData"
              />
              <p class="mt-2 mb-0 fs-11 text-center">
                Trading pairs are comprised of "BASE/QUOTE" (e.g. BTC/USDT). This chart shows how much you have allocated to the BASE so you know how your portfolio is allocated before you own those coins (e.g 100 USDT on BTC/USDT counts towards BTC). Helps to build a risk adjusted portfolio.
                Based on current Spot Assets & Positions (incl. unrealized P&L) valued in USD. Rounded to nearest number.
              </p>
            </div>
          </div>

          <!-- Allocated categories in % -->
          <div v-if="userIsAutoTradingOrTelegramAdminMix" class="card mb-3">
            <div class="card-header">
              <h5 class="fs-6 mb-0 text-nowrap py-2 py-xl-0">Funds per Bot Category</h5>
            </div>
            <div class="card-body">
              <PieChartWithLegend
                :chartData="usdFundsPerBotCategoryPieChartData"
              />
              <p class="mt-2 mb-0 fs-11 text-center">Based on current Spot Assets & Positions (incl. unrealized P&L) valued in USD. Rounded to nearest number.</p>
            </div>
          </div>
          
          <!-- Exchanges in % -->
          <div class="card">
            <div class="card-header">
              <h5 class="fs-6 mb-0 text-nowrap py-2 py-xl-0">Funds per Exchange</h5>
            </div>
            <div class="card-body">
              <PieChartWithLegend
                :chartData="usdFundsPerExchangePieChartData"
              />
              <p class="mt-2 mb-0 fs-11 text-center">Based on current Spot Assets & Positions (incl. unrealized P&L) valued in USD. Rounded to nearest number.</p>
            </div>
          </div>

        </div>
        <div class="col-12 col-xl-6 col-lg-12 col-md-12">

          <!-- Assets in USD -->
          <div class="card mb-3">
            <div class="card-header">
              <h5 class="fs-6 mb-0 text-nowrap py-2 py-xl-0">Funds (USD)</h5>
              <p class="mt-2 mb-0 fs-11 text-start">Based on current Spot Assets & Positions (incl. unrealized P&L).</p>
            </div>
            <div class="card-body p-0">
              
              <div class="table-responsive scrollbar">
                <table class="table table-hover table-sm table-striped fs-10 mb-0 overflow-hidden">
                  <thead class="bg-200">
                    <tr>
                      <th class="text-900 pe-1 align-middle white-space-nowrap">
                        Asset
                      </th>
                      <th class="text-900 pe-1 align-middle white-space-nowrap sortable" @click="sortFundsTable('balanceUSD')">
                        Balance USD <span v-html="getFundsSortIcon('balanceUSD')"></span>
                      </th>
                      <th class="text-900 pe-1 align-middle white-space-nowrap sortable" @click="sortFundsTable('balance')">
                        Balance <span v-html="getFundsSortIcon('balance')"></span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="list" id="table-orders-body">
                    <tr v-for="asset in userFundsPerAssetSorted" :key="asset.coin" class="">
                      <td class="pe-2 py-2 align-middle white-space-nowrap">
                        <span class="fw-bold">{{ asset.coin }}</span>
                      </td>
                      <td class="py-2 align-middle white-space-nowrap">
                        {{ getPrettyFloatNumber(asset.balanceUSD, 2) }}
                      </td>
                      <td class="pe-1 py-2 align-middle white-space-nowrap">
                        {{ getPrettyFloatNumber(asset.balance, 4) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>
          </div>

          <!-- Bot Assets in USD -->
          <div class="card mb-3">
            <div class="card-header">
              <h5 class="fs-6 mb-0 text-nowrap py-2 py-xl-0">Spot Assets per Bot (USD)</h5>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive scrollbar">
                <table class="table table-hover table-sm table-striped fs-10 mb-0 overflow-hidden">
                  <thead class="bg-200">
                    <tr>
                      <th class="text-900 pe-1 align-middle white-space-nowrap sortable" @click="sortBotAssetsTable('botTitle')">
                        Bot Title <span v-html="getBotAssetsSortIcon('botTitle')"></span>
                      </th>
                      <th class="text-900 pe-1 align-middle white-space-nowrap sortable" @click="sortBotAssetsTable('coin')">
                        Asset <span v-html="getBotAssetsSortIcon('coin')"></span>
                      </th>
                      <th class="text-900 pe-1 align-middle white-space-nowrap sortable" @click="sortBotAssetsTable('balanceUSD')">
                        Balance USD <span v-html="getBotAssetsSortIcon('balanceUSD')"></span>
                      </th>
                      <th class="text-900 pe-1 align-middle white-space-nowrap sortable" @click="sortBotAssetsTable('balance')">
                        Balance <span v-html="getBotAssetsSortIcon('balance')"></span>
                      </th>
                      <th class="text-900 pe-1 align-middle white-space-nowrap sortable" @click="sortBotAssetsTable('status')">
                        Bot Status <span v-html="getBotAssetsSortIcon('status')"></span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="list" id="table-orders-body">
                    <tr v-for="botAsset in allAssetsPerBotSorted" :key="botAsset.botId+botAsset.coin">
                      <td class="pe-2 py-2 align-middle white-space-nowrap">
                        <img class="rounded mx-auto" style="height: 20px" :src="`/exchanges/${botAsset.botExchangeId.toLowerCase()}.png`" />&nbsp;
                        <router-link :to="{name: 'EditBot', params: {id: botAsset.botId}}">
                          <span :class="'fw-bold '+getBotLineClassNames(botAsset.botId)">{{ botAsset.botTitle }}</span>
                        </router-link> <span v-if="botAsset.botCategory" title="Category">{{ botAsset.botCategory }}</span>
                      </td>
                      <td class="pe-2 py-2 align-middle white-space-nowrap">
                        <span :class="'fw-bold '+getBotLineClassNames(botAsset.botId)">{{ botAsset.coin }}</span>
                      </td>
                      <td class="py-2 align-middle white-space-nowrap">
                        <span :class="' '+getBotLineClassNames(botAsset.botId)">{{ getPrettyFloatNumber(botAsset.balanceUSD, 2) }}</span>
                      </td>
                      <td class="py-2 align-middle white-space-nowrap">
                        <span :class="' '+getBotLineClassNames(botAsset.botId)">{{ getPrettyFloatNumber(botAsset.balance, 4) }}</span>
                      </td>
                      <td class="pe-2 py-2 align-middle white-space-nowrap">
                        <span :class="' '+getBotLineClassNames(botAsset.botId)">{{ (getBotById(botAsset.botId)?.enabled)? 'Active' : 'Paused' }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Bot Positions in USD -->
          <div v-if="allPositionsPerBot?.length > 0" class="card">
            <div class="card-header">
              <h5 class="fs-6 mb-0 text-nowrap py-2 py-xl-0">Positions per Bot (USD)</h5>
            </div>
            <div class="card-body p-0">
              
              <div class="table-responsive scrollbar">
                <table class="table table-hover table-sm table-striped fs-10 mb-0 overflow-hidden">
                  <thead class="bg-200">
                    <tr>
                      <th class="text-900 align-middle white-space-nowrap sortable" @click="sortBotPositionsTable('botTitle')">
                        Bot Title <span v-html="getBotPositionsSortIcon('botTitle')"></span>
                      </th>
                      <th class="text-900 align-middle white-space-nowrap sortable" @click="sortBotPositionsTable('baseCoin')">
                        Asset <span v-html="getBotPositionsSortIcon('baseCoin')"></span>
                      </th>
                      <th class="text-900 align-middle white-space-nowrap sortable" @click="sortBotPositionsTable('leverage')">
                        Leverage <span v-html="getBotPositionsSortIcon('leverage')"></span>
                      </th>
                      <th class="text-900 align-middle white-space-nowrap sortable" @click="sortBotPositionsTable('direction')">
                        Side <span v-html="getBotPositionsSortIcon('direction')"></span>
                      </th>
                      <th class="text-900 align-middle white-space-nowrap sortable" @click="sortBotPositionsTable('collateralUSD')">
                        Collateral USD <span v-html="getBotPositionsSortIcon('collateralUSD')"></span>
                      </th>
                      <th class="text-900 align-middle white-space-nowrap sortable" @click="sortBotPositionsTable('unrealizedPnl')">
                        Unrealized P&L USD <span v-html="getBotPositionsSortIcon('unrealizedPnl')"></span>
                      </th>
                      <th class="text-900 pe-1 align-middle white-space-nowrap sortable" @click="sortBotPositionsTable('status')">
                        Bot Status <span v-html="getBotPositionsSortIcon('status')"></span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="list">
                    <tr v-for="botPosition in allPositionsPerBotSorted" :key="botPosition" class="">
                      <td class="pe-2 py-2 align-middle white-space-nowrap">
                        <img class="rounded mx-auto" style="height: 20px" :src="`/exchanges/${botPosition.botExchangeId.toLowerCase()}.png`" />&nbsp;
                        <router-link :to="{name: 'EditBot', params: {id: botPosition.botId}}">
                          <span :class="'fw-bold '+getBotLineClassNames(botPosition.botId)">{{ botPosition.botTitle }}</span>
                        </router-link>
                      </td>
                      <td class="pe-3 py-2 align-middle white-space-nowrap">
                        <span class="fw-bold">{{ botPosition.baseCoin }}</span>
                      </td>
                      <td class="py-2 align-middle white-space-nowrap">
                        {{ botPosition.leverage }}x
                      </td>
                      <td class="py-2 align-middle white-space-nowrap">
                        {{ botPosition.direction }}
                      </td>
                      <td class="py-2 align-middle white-space-nowrap">
                        {{ getPrettyFloatNumber(botPosition.currentCollateralAsQuoteCoinUSD, 2) }} USD
                      </td>
                      <td class="py-2 align-middle white-space-nowrap">
                        {{ getPrettyFloatNumber(botPosition.unrealizedPnlAsQuoteCoinUSD, 4) }} USD ({{ getPrettyFloatNumber(botPosition.unrealizedPnlAsPercentage, 4) }}%)
                      </td>
                      <td class="py-2 align-middle white-space-nowrap">
                        <span :class="' '+getBotLineClassNames(botPosition.botId)">{{ (getBotById(botPosition.botId)?.enabled)? 'Active' : 'Paused' }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>
          </div>

        </div>
      </div> <!-- row -->

    </div> <!-- !loading -->

  </div>

</template>

<script>
import { userHelperMixin } from '@/mixins/userHelperMixin'
import TheNavbar from '@/components/TheNavbar'
import TheBotErrorsAlert from '@/components/TheBotErrorsAlert'
import PieChartWithLegend from '@/components/PieChartWithLegend'

export default {
  mixins: [userHelperMixin],
  components: {
    TheNavbar,
    TheBotErrorsAlert,
    PieChartWithLegend
  },
  data () {
    return {
      loadingUserAssets: false,
      loadingBotAssets: false,
      showDust: false,
      dustThreshold: 1, // USD
      // Sort states for Funds table
      fundsSortBy: 'balanceUSD',
      fundsSortDirection: 'desc',
      // Sort states for Bot Assets table
      botAssetsSortBy: 'botTitle',
      botAssetsSortDirection: 'asc',
      // Sort states for Bot Positions table
      botPositionsSortBy: 'botTitle',
      botPositionsSortDirection: 'asc'
    }
  },
  computed: {
    // TODO: Only used to display a warning message for the Coinbase API having issues. Can be removed when not used anywhere anymore.
    hasCoinbaseBot () {
      const bots = this.$store.getters.getUserBots
      return Array.isArray(bots) && bots.some(b => b.exchangeId === 'coinbase')
    },
    nowFetchingBot () {
      return this.$store.state.user?.dashboard?.nowFetchingBot
    },
    // Deduped and dust filtered
    unifiedDashboardData () {
      const data = this.$store.getters.getUserDashboardUnifiedAssetData
      if (!data || data.length <= 0) return []

      const deduped = new Map()

      for (const entry of data) {
        const dustCheckUSD = entry.type === 'asset' ? (entry.balanceUSD || 0) : (entry.currentCollateralAsQuoteCoinUSD || 0)

        // Apply dust filter at entry level. Negative balances can occurr due to Futures, thus we use Math.abs() to filter true dust whether it's + or -.
        if (!this.showDust && Math.abs(dustCheckUSD) < this.dustThreshold) continue

        const existing = deduped.get(entry.dedupeKey)
        if (existing) {
          // Do nothing, which is the whole point here so we don't double count funds from the same api key again (dedup key)
        } else {
          deduped.set(entry.dedupeKey, { ...entry })
        }
      }

      return Array.from(deduped.values())
    },
    userFundsPerAsset () {
      const data = this.unifiedDashboardData
      if (!data || data.length <= 0) return []

      // Group by coin and sum
      const byCoin = new Map()

      for (const item of data) {
        const existing = byCoin.get(item.coin)
        if (existing) {
          existing.balance += Number(item.balance)
          existing.balanceUSD += Number(item.balanceUSD)
        } else {
          byCoin.set(item.coin, { ...item })
        }
      }

      // Filter final grouped results by dust threshold
      const result = Array.from(byCoin.values())
        .sort((a, b) => b.balanceUSD - a.balanceUSD) // Sort here so it's always sorted DESC

      return result
    },
    userFundsPerAssetSorted () {
      const sorted = [...this.userFundsPerAsset]

      sorted.sort((a, b) => {
        let aVal, bVal
        
        switch(this.fundsSortBy) {
          case 'balanceUSD':
            aVal = parseFloat(a.balanceUSD) || 0
            bVal = parseFloat(b.balanceUSD) || 0
            break
          case 'balance':
            aVal = parseFloat(a.balance) || 0
            bVal = parseFloat(b.balance) || 0
            break
          default:
            aVal = a.botTitle
            bVal = b.botTitle
        }
        
        if (aVal < bVal) return this.fundsSortDirection === 'asc' ? -1 : 1
        if (aVal > bVal) return this.fundsSortDirection === 'asc' ? 1 : -1
        return 0
      })
      
      return sorted
    },
    allPositionsPerBot () {
      const data = this.unifiedDashboardData
      if (!data || data.length <= 0) return []

      return data
        .filter(entry => entry.type === 'position')
        .map(position => {
          const bot = this.getBotById(position.botId)
          return {
            ...position,
            botTitle: bot?.title,
            botCategory: bot?.category,
            botExchangeId: bot?.exchangeId
          }
        })
        .sort((a, b) => String(b.botId).localeCompare(String(a.botId))) // Sort here so it's always sorted DESC
    },
    allPositionsPerBotSorted () {
      const sorted = [...this.allPositionsPerBot]
      
      sorted.sort((a, b) => {
        let aVal, bVal
        
        switch(this.botPositionsSortBy) {
          case 'botTitle':
            aVal = a.botTitle
            bVal = b.botTitle
            break
          case 'baseCoin':
            aVal = a.baseCoin
            bVal = b.baseCoin
            break
          case 'leverage':
            aVal = parseFloat(a.leverage) || 0
            bVal = parseFloat(b.leverage) || 0
            break
          case 'direction':
            aVal = a.direction
            bVal = b.direction
            break
          case 'collateralUSD':
            aVal = parseFloat(a.currentCollateralAsQuoteCoinUSD) || 0
            bVal = parseFloat(b.currentCollateralAsQuoteCoinUSD) || 0
            break
          case 'unrealizedPnl':
            aVal = parseFloat(a.unrealizedPnlAsQuoteCoinUSD) || 0
            bVal = parseFloat(b.unrealizedPnlAsQuoteCoinUSD) || 0
            break
          case 'status':
            aVal = this.getBotById(a.botId)?.enabled ? 'Active' : 'Paused'
            bVal = this.getBotById(b.botId)?.enabled ? 'Active' : 'Paused'
            break
          default:
            aVal = a.botTitle
            bVal = b.botTitle
        }

        if (aVal < bVal) return this.botPositionsSortDirection === 'asc' ? -1 : 1
        if (aVal > bVal) return this.botPositionsSortDirection === 'asc' ? 1 : -1
        return 0
      })
      
      return sorted
    },
    allAssetsPerBot () {
      const data = this.unifiedDashboardData
      if (!data || data.length <= 0) return []

      return data
        .filter(entry => entry.type === 'asset')
        .map(asset => {
          const bot = this.getBotById(asset.botId)
          return {
            ...asset,
            botTitle: bot?.title,
            botCategory: bot?.category,
            botExchangeId: bot?.exchangeId
          }
        })
        .sort((a, b) => String(b.botId).localeCompare(String(a.botId))) // Sort here so it's always sorted DESC
    },
    allAssetsPerBotSorted () {
      const sorted = [...this.allAssetsPerBot]
      
      sorted.sort((a, b) => {
        let aVal, bVal
        
        switch(this.botAssetsSortBy) {
          case 'botTitle':
            aVal = a.botTitle
            bVal = b.botTitle
            break
          case 'coin':
            aVal = a.coin
            bVal = b.coin
            break
          case 'balanceUSD':
            aVal = parseFloat(a.balanceUSD) || 0
            bVal = parseFloat(b.balanceUSD) || 0
            break
          case 'balance':
            aVal = parseFloat(a.balance) || 0
            bVal = parseFloat(b.balance) || 0
            break
          case 'status':
            aVal = this.getBotById(a.botId)?.enabled ? 'Active' : 'Paused'
            bVal = this.getBotById(b.botId)?.enabled ? 'Active' : 'Paused'
            break
          default:
            aVal = a.botTitle
            bVal = b.botTitle
        }

        if (aVal < bVal) return this.botAssetsSortDirection === 'asc' ? -1 : 1
        if (aVal > bVal) return this.botAssetsSortDirection === 'asc' ? 1 : -1
        return 0
      })
      
      return sorted
    },
    fundAllocationPerAsset () {
      const allUserBotAssets = this.allAssetsPerBot
      const allUserBotPositions = this.allPositionsPerBot

      let userMergedAssets = {}

      // Iterate over assets
      for (const asset of allUserBotAssets) {
        const balanceUSD = asset.balanceUSD // Math.round(asset.balanceUSD) is a problem for 0.5 USD which get's rounded to 1 USD. Rounding here does not make sense.

        const botId = asset.botId
        const bot = this.getBotById(botId)
        if (!bot) continue

        // INFO: How to handle coins which are not part of the trading pair but are also on the account? Because "total assets value in USD" counts them, so % would be wrong if we don't count them. Will count them and see what happens.

        // Create the object of allocated assets aka. bot.baseCoin
        if (!userMergedAssets[bot.baseCoin]) {
          userMergedAssets[bot.baseCoin] = {
            title: bot.baseCoin,
            balanceUSD: balanceUSD
          }
        } else {
          userMergedAssets[bot.baseCoin].balanceUSD += balanceUSD
        }
      }

      // Iterate over positions
      for (const position of allUserBotPositions) {
        const balanceUSD = position.currentCollateralAsQuoteCoinUSD // TODO: add P&L when API allows me. Math.round(asset.balanceUSD) is a problem for 0.5 USD which get's rounded to 1 USD. Rounding here does not make sense.

        // Filter the dust
        if (!this.showDust && balanceUSD < 1) continue

        const botId = position.botId
        const bot = this.getBotById(botId)
        if (!bot) continue

        // INFO: How to handle coins which are not part of the trading pair but are also on the account? Because "total assets value in USD" counts them, so % would be wrong if we don't count them. Will count them and see what happens.

        // Create the object of allocated assets aka. bot.baseCoin
        if (!userMergedAssets[bot.baseCoin]) {
          userMergedAssets[bot.baseCoin] = {
            title: bot.baseCoin,
            balanceUSD: balanceUSD
          }
        } else {
          userMergedAssets[bot.baseCoin].balanceUSD += balanceUSD
        }
      }

      // Convert to nummeric array so it can be sorted later on
      userMergedAssets = Object
        .entries(userMergedAssets)
        .map(([i, o]) => ({ i, ...o }));

      // Sort it based on the USD value DESC
      userMergedAssets.sort((a, b) => b.balanceUSD - a.balanceUSD) // Sorting here because we always need it sorted this way anyhow

      return userMergedAssets
    },
    usdFundsPerExchange () {
      const data = this.unifiedDashboardData
      if (!data || data.length <= 0) return []

      const byExchange = new Map()

      for (const entry of data) {
        const valueUSD = entry.type === 'asset' ? (entry.balanceUSD || 0) : (entry.currentCollateralAsQuoteCoinUSD || 0)

        const existing = byExchange.get(entry.exchangeId)
        if (existing) {
          existing.balanceUSD += valueUSD
        } else {
          byExchange.set(entry.exchangeId, {
            exchangeId: entry.exchangeId,
            balanceUSD: valueUSD
          })
        }
      }

      // Final sorted result
      return Array.from(byExchange.values())
        .sort((a, b) => b.balanceUSD - a.balanceUSD) // Sort here so it's always sorted DESC
    },
    usdFundsPerBotCategory () {
      const data = this.unifiedDashboardData
      if (!data || data.length <= 0) return []

      const byCategory = new Map()

      for (const entry of data) {
        const valueUSD = entry.type === 'asset' ? (entry.balanceUSD || 0) : (entry.currentCollateralAsQuoteCoinUSD || 0)

        const key = entry.botCategory ?? 'null'
        const existing = byCategory.get(key)
        if (existing) {
          existing.balanceUSD += valueUSD
        } else {
          byCategory.set(key, {
            botCategory: entry.botCategory,
            balanceUSD: valueUSD
          })
        }
      }

      // Final sorted result
      return Array.from(byCategory.values())
        .sort((a, b) => b.balanceUSD - a.balanceUSD) // Sort here so it's always sorted DESC
    },
    totalAssetsValueUSD () {
      if (this.allAssetsPerBot.length === 0 && this.allPositionsPerBot.length === 0) return 0

      let total = 0
      
      for (const asset of this.allAssetsPerBot) {
        total += asset.balanceUSD || 0
      }

      for (const position of this.allPositionsPerBot) {
        total += position.currentCollateralAsQuoteCoinUSD || 0
      }
      
      return total
    },
    userFundsPerAssetPieChartData () {
      // Init data strcuture otherwise ChartJs crashes
      let data = {
        labels: ['Loading'],
        datasets: [
          {
            data: [100],
            hoverOffset: 12
          }
        ]
      }

      if (!this.userFundsPerAsset) return data
      const userFundsPerAsset = [...this.userFundsPerAsset].sort((a, b) => b.balanceUSD - a.balanceUSD) // Sort here to be sure it's correct no matter how you receive it

      // Reset values
      data.labels = []
      data.datasets[0].data = []
      const totalAssetsValueUSD = this.totalAssetsValueUSD

      // Now fill the values in
      for (const asset of userFundsPerAsset) {
        const balancePercent = Number.parseFloat(asset.balanceUSD/totalAssetsValueUSD * 100).toFixed(2)
        // let balanceUSD = this.getPrettyFloatNumber(asset.balanceUSD, 2)
        // balanceUSD = balanceUSD.replace(",", "")

        data.labels.push(asset.coin+" ("+balancePercent+"%)" )
        data.datasets[0].data.push(balancePercent)
      }

      return data
    },
    usdFundsPerExchangePieChartData () {
      // Init data strcuture otherwise ChartJs crashes
      let data = {
        labels: ['Loading'],
        datasets: [
          {
            data: [100],
            hoverOffset: 12
          }
        ]
      }
      
      if (!this.usdFundsPerExchange) return data
      const usdFundsPerExchange = [...this.usdFundsPerExchange].sort((a, b) => b.balanceUSD - a.balanceUSD) // Sort here to be sure it's correct no matter how you receive it

      // Reset values
      data.labels = []
      data.datasets[0].data = []
      const totalAssetsValueUSD = this.totalAssetsValueUSD

      // Now fill the values in
      for (const exchangeUsd of usdFundsPerExchange) {
        const balancePercent = Number.parseFloat(exchangeUsd.balanceUSD/totalAssetsValueUSD * 100).toFixed(2)
        const balanceUSD = this.getPrettyFloatNumber(exchangeUsd.balanceUSD, 2)
        // balanceUSD = balanceUSD.replace(",", "")
        
        if (balancePercent >= 0.01 || this.showDust) {
          const exchangeObj = this.getExchangeById(exchangeUsd.exchangeId)

          data.labels.push(exchangeObj.name+" ("+balancePercent+"%) ("+balanceUSD+" USD)" )
          data.datasets[0].data.push(balancePercent)
        }
      }

      return data
    },
    usdFundsPerBotCategoryPieChartData () {
      // Init data strcuture otherwise ChartJs crashes
      let data = {
        labels: ['Loading'],
        datasets: [
          {
            data: [100],
            hoverOffset: 12
          }
        ]
      }
      
      if (!this.usdFundsPerBotCategory) return data
      const usdFundsPerBotCategory = [...this.usdFundsPerBotCategory].sort((a, b) => b.balanceUSD - a.balanceUSD) // Sort here to be sure it's correct no matter how you receive it

      // Reset values
      data.labels = []
      data.datasets[0].data = []
      const totalAssetsValueUSD = this.totalAssetsValueUSD

      // Now fill the values in
      for (const botCategoryUsd of usdFundsPerBotCategory) {
        const balancePercent = Number.parseFloat(botCategoryUsd.balanceUSD/totalAssetsValueUSD * 100).toFixed(2)
        const balanceUSD = this.getPrettyFloatNumber(botCategoryUsd.balanceUSD, 2)
        // balanceUSD = balanceUSD.replace(",", "")
        
        if (balancePercent >= 0.01 || this.showDust) {
          // const exchangeObj = this.getExchangeById(botCategoryUsd.botCategory)
          let categoryName = botCategoryUsd.botCategory
          if (!categoryName || categoryName == "null") categoryName = "Other"

          data.labels.push(categoryName+" ("+balancePercent+"%) ("+balanceUSD+" USD)" )
          data.datasets[0].data.push(balancePercent)
        }
      }

      return data
    },  
    allUserAllocatedFundsPieChartData () {
      // Init data strcuture otherwise ChartJs crashes
      let data = {
        labels: ['Loading'],
        datasets: [
          {
            data: [100],
            hoverOffset: 12
          }
        ]
      }

      if (!this.fundAllocationPerAsset) return data
      const fundAllocationPerAsset = [...this.fundAllocationPerAsset].sort((a, b) => b.balanceUSD - a.balanceUSD) // Sort here to be sure it's correct no matter how you receive it

      // Reset values
      data.labels = []
      data.datasets[0].data = []
      const totalAssetsValueUSD = this.totalAssetsValueUSD

      // Now fill the values in
      for (const asset of fundAllocationPerAsset) {
        const balancePercent = Number.parseFloat(asset.balanceUSD/totalAssetsValueUSD * 100).toFixed(2)

        if (balancePercent >= 0.01 || this.showDust) {
          data.labels.push(asset.title+" ("+balancePercent+"%)" )
          data.datasets[0].data.push(balancePercent)
        }
      }

      return data
    },
    protectPrivacy () {
      return this.$store.getters.getProtectPrivacy
    }
  },
  methods: {
    // Funds table sorting
    sortFundsTable(field) {
      if (this.fundsSortBy === field) {
        this.fundsSortDirection = this.fundsSortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.fundsSortBy = field
        this.fundsSortDirection = 'asc'
      }
    },
    getFundsSortIcon(field) {
      if (this.fundsSortBy !== field) return ''
      return this.fundsSortDirection === 'asc' ? '&#9650;' : '&#9660;'
    },
    // Bot Assets table sorting
    sortBotAssetsTable(field) {
      if (this.botAssetsSortBy === field) {
        this.botAssetsSortDirection = this.botAssetsSortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.botAssetsSortBy = field
        this.botAssetsSortDirection = 'asc'
      }
    },
    getBotAssetsSortIcon(field) {
      if (this.botAssetsSortBy !== field) return ''
      return this.botAssetsSortDirection === 'asc' ? '&#9650;' : '&#9660;'
    },
    // Bot Positions table sorting
    sortBotPositionsTable(field) {
      if (this.botPositionsSortBy === field) {
        this.botPositionsSortDirection = this.botPositionsSortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.botPositionsSortBy = field
        this.botPositionsSortDirection = 'asc'
      }
    },
    getBotPositionsSortIcon(field) {
      if (this.botPositionsSortBy !== field) return ''
      return this.botPositionsSortDirection === 'asc' ? '&#9650;' : '&#9660;'
    },
    getBotById (id) {
      const bots = this.$store.getters.getUserBots
      return bots.find(elem => elem.id == id)
    },
    getExchangeById (id) {
      const platformExchanges = this.$store.getters.getPlatformExchanges
      return platformExchanges?.find(elem => elem.id == id)
    },
    async refreshUserAssets() {
      this.loadingUserAssets = true
      await this.$store.dispatch('fetchAllUserDashboardAssets')
      this.loadingUserAssets = false
    },
    getPrettyFloatNumber (number, numDecimals) {
      let formattedNumber = parseFloat(number).toLocaleString('en-US', {minimumFractionDigits: numDecimals, maximumFractionDigits: numDecimals})
      
      if (this.protectPrivacy) {
        formattedNumber = 'x.xxxx'
      }

      return formattedNumber
    },
    getBotLineClassNames (botId) {
      const bot = this.getBotById(botId)
      if (!bot) return ''
      let classNames = []

      if (!bot.enabled){
        classNames.push('text-warning')
      }

      return classNames.join(' ')
    }
  },
  async created () {
    this.loadingUserAssets = true
    if (!this.$store.getters.getPlatformExchanges) await this.$store.dispatch('getPlatformExchanges')
    if (!this.$store.getters.getUserDashboardUnifiedAssetData) await this.$store.dispatch('fetchAllUserDashboardAssets') // Also loads getAllUserBots fyi
    this.loadingUserAssets = false
  },
  beforeMount () {
    // Redirect to Login when the user is not logged in
    if (!this.$store.getters.isUserLoggedIn) this.$router.push({ name: 'Login' })
  }
}
</script>

<style scoped>
.sortable {
  cursor: pointer;
  user-select: none;
}
.sortable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>